import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Box, Button, Select, MenuItem, Chip, LinearProgress, Snackbar, Alert, CircularProgress
} from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import io from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'https://pochelo.online';

const COLOR_ESTADO = {
    enviado: 'info',
    descargando: 'info',
    progreso: 'info',
    instalado: 'info',
    exito: 'success',
    ya_actualizado: 'success',
    rollback: 'warning',
    error: 'error'
};

const EN_CURSO = ['enviado', 'descargando', 'progreso', 'instalado'];

const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatoFecha = (fecha) => fecha ? new Date(fecha).toLocaleString('es-CO') : '—';

// Solo los firmwares del modelo del equipo; si el equipo no reporta modelo (firmware viejo) se muestran todos
const firmwaresDe = (firmwares, modelo) => modelo ? firmwares.filter(f => f.nombre === modelo) : firmwares;

const OtaFirmware = () => {
    const [dispositivos, setDispositivos] = useState({});
    const [firmwares, setFirmwares] = useState([]);
    const [seleccion, setSeleccion] = useState({});
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const mostrarSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const [resDisp, resFw] = await Promise.all([
                fetch('/api/ota/dispositivos', { headers: authHeaders() }),
                fetch('/api/ota/firmwares', { headers: authHeaders() })
            ]);
            if (!resDisp.ok || !resFw.ok) throw new Error('No autorizado o error del servidor');
            const lista = await resDisp.json();
            setDispositivos(Object.fromEntries(lista.map(d => [d.chip, d])));
            setFirmwares(await resFw.json());
        } catch (error) {
            mostrarSnackbar(`Error al cargar: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        cargar();
        const socket = io(SOCKET_URL);
        socket.on('otaDispositivo', (d) => setDispositivos(prev => ({ ...prev, [d.chip]: d })));
        return () => socket.disconnect();
    }, [cargar]);

    const actualizar = async (chip) => {
        const archivo = seleccion[chip];
        if (!archivo) return;
        const d = dispositivos[chip];
        const aviso = d.modelo ? '' : '\n\nEste equipo no reporta su modelo: verifica que el firmware sea el correcto.';
        if (!window.confirm(`¿Actualizar ${d.codigo || chip} (fw ${d.fw}) con ${archivo}?${aviso}`)) return;
        try {
            const res = await fetch('/api/ota/actualizar', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ chip, archivo })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            mostrarSnackbar(`Orden enviada a ${d.codigo || chip}`);
        } catch (error) {
            mostrarSnackbar(`Error: ${error.message}`, 'error');
        }
    };

    const lista = Object.values(dispositivos).sort((a, b) => (a.codigo || '').localeCompare(b.codigo || ''));

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Actualización de firmware (OTA)</Typography>
                <Button startIcon={<RefreshIcon />} onClick={cargar}>Recargar</Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sube el binario a /var/www/firmware con el nombre &lt;nombre&gt;_v&lt;x.y.z&gt;.bin.
                La versión del nombre debe coincidir con FW_VERSION del firmware.
                Prueba primero en un solo equipo.
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Equipo</TableCell>
                                <TableCell>Chip</TableCell>
                                <TableCell>Firmware</TableCell>
                                <TableCell>RSSI</TableCell>
                                <TableCell>Última OTA</TableCell>
                                <TableCell>Nuevo firmware</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lista.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        Ningún equipo con soporte OTA reportado todavía
                                    </TableCell>
                                </TableRow>
                            )}
                            {lista.map(d => {
                                const ota = d.ota;
                                const enCurso = ota && EN_CURSO.includes(ota.estado);
                                return (
                                    <TableRow key={d.chip}>
                                        <TableCell>
                                            {d.codigo || '—'}
                                            <Typography variant="caption" color={d.modelo ? 'text.secondary' : 'warning.main'} display="block">
                                                {d.modelo || 'modelo sin reportar'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{d.chip}</TableCell>
                                        <TableCell>{d.fw || '—'}</TableCell>
                                        <TableCell>{d.rssi != null ? `${d.rssi} dBm` : '—'}</TableCell>
                                        <TableCell sx={{ minWidth: 200 }}>
                                            {ota ? (
                                                <>
                                                    <Chip size="small" color={COLOR_ESTADO[ota.estado] || 'default'}
                                                        label={`${ota.estado} ${ota.version || ''}`} />
                                                    {ota.estado === 'progreso' && (
                                                        <LinearProgress variant="determinate" value={ota.progreso || 0} sx={{ mt: 1 }} />
                                                    )}
                                                    {ota.detalle && (
                                                        <Typography variant="caption" display="block">{ota.detalle}</Typography>
                                                    )}
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {formatoFecha(ota.fecha)}
                                                    </Typography>
                                                </>
                                            ) : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Select size="small" displayEmpty sx={{ minWidth: 220 }}
                                                value={seleccion[d.chip] || ''}
                                                onChange={e => setSeleccion({ ...seleccion, [d.chip]: e.target.value })}>
                                                <MenuItem value=""><em>Elegir…</em></MenuItem>
                                                {firmwaresDe(firmwares, d.modelo).map(f => (
                                                    <MenuItem key={f.archivo} value={f.archivo}>
                                                        {f.archivo} ({(f.tamano / 1024).toFixed(0)} KB)
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </TableCell>
                                        <TableCell>
                                            <Button variant="contained" size="small" startIcon={<SystemUpdateAltIcon />}
                                                disabled={!seleccion[d.chip] || enCurso}
                                                onClick={() => actualizar(d.chip)}>
                                                Actualizar
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default OtaFirmware;
